/**
 * The prices of one article under every tariff, opened from the list's price.
 *
 * The same fiche read, and the same cards, as the prices block of the article
 * page — and, like the stock dialog, served from what was prefetched on the way
 * to the click.
 */

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useResource } from "@hardauto/core-data";
import { money } from "@hardauto/core-i18n";
import { Dialog, ErrorState, LoadingState } from "@hardauto/core-ui";

import type { ConsultationArticle, ConsultationRow } from "../../api";
import { NAMESPACE } from "../../i18n";
import { PriceStateBadge } from "./cells";

/** The columns: the base price, then the tariffs in the order the administrators ranked them. */
function columns(article: ConsultationArticle) {
  return [
    ...article.prices.filter((line) => line.tariff === null),
    ...article.prices.filter((line) => line.tariff !== null),
  ];
}

export function PriceDialog({
  load,
  row,
  onClose,
}: {
  load: (id: string) => Promise<ConsultationArticle>;
  row: ConsultationRow;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const fetcher = useCallback(() => load(row.id), [load, row.id]);
  const article = useResource(fetcher);

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${row.reference} · ${t("consultation.priceDialog.title")}`}
      description={row.designation}
      closeLabel={t("consultation.stockDialog.close")}
    >
      <div className="hardauto-stock-dialog">
        {article.failed ? (
          <ErrorState
            title={t("error.title")}
            onRetry={article.reload}
            retryLabel={t("error.retry")}
          />
        ) : article.data ? (
          <div className="hardauto-pricegrid">
            <table>
              <thead>
                <tr>
                  {columns(article.data).map((line) => (
                    <th key={line.tariff?.code ?? "__base__"} scope="col">
                      {line.tariff ? line.tariff.label : t("consultation.basePrice")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {columns(article.data).map((line) => (
                    <td key={line.tariff?.code ?? "__base__"}>
                      {line.state === "priced" ? (
                        <span className="hardauto-num hardauto-num--strong">
                          {money(line.unit_price, i18n.language)}
                        </span>
                      ) : (
                        <PriceStateBadge
                          state={line.state === "out_of_perimeter" ? line.state : "missing"}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <LoadingState label={t("consultation.article.loading")} />
        )}
      </div>
    </Dialog>
  );
}
